import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Button,
  Input,
  Chip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Tabs,
  Tab,
  Textarea,
  Tooltip,
} from "@nextui-org/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSave,
  faChevronDown,
  faChevronRight,
  faLink,
  faUnlink,
  faRobot,
  faComments,
  faUpload,
  faTree,
  faPaste,
} from "@fortawesome/free-solid-svg-icons";
import toast, { Toaster } from "react-hot-toast";
import BreadCrumbsPage from "../../components/BreadCrumbsPage";
import LoadingPage from "../../components/Loader/LoadingPage";
import { PublishData, getDataWithFilterUrl, getAlldata, DeleteOnedata } from "../../controllers/APIController";
import SearchIcon from "../../Images/Search";
import { StandardCancelButton, StandardPrimaryButton, StandardCloseButton } from "../../components/StandardButtons";

// Report Structure Component
const ReportStructure = () => {
  const [reportStructure, setReportStructure] = useState([]);
  const [availableMetrics, setAvailableMetrics] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedSections, setExpandedSections] = useState(new Set());
  
  const { 
    isOpen: isSectionModalOpen, 
    onOpen: onSectionModalOpen, 
    onOpenChange: onSectionModalChange 
  } = useDisclosure();
  
  const { 
    isOpen: isMetricModalOpen, 
    onOpen: onMetricModalOpen, 
    onOpenChange: onMetricModalChange 
  } = useDisclosure();

  const [newSection, setNewSection] = useState({
    title: "",
    description: "",
    type: "section", // section, subsection, metric
    parentId: null,
  });



  // Utility function to validate user session
  const validateUserSession = () => {
    try {
      const user = JSON.parse(sessionStorage.getItem("userData"));
      
      if (!user) {
        toast.error("Session expired. Please refresh the page and log in again.", {
          duration: 5000,
        });
        return false;
      }
      
      // Check if we have organisation data (either from sessionStorage or user object)
      const organisation = JSON.parse(sessionStorage.getItem("organisation"));
      const hasOrganisation = organisation || (user.user && (user.user.LastOrgId || user.user.organisation));
      
      if (!hasOrganisation) {
        toast.error("Organisation data not found. Please refresh the page and log in again.", {
          duration: 5000,
        });
        return false;
      }
      
      return true;
    } catch (error) {
      console.error("Error validating user session:", error);
      toast.error("Session validation failed. Please refresh the page and log in again.", {
        duration: 5000,
      });
      return false;
    }
  };

  // Load report structure and metrics on component mount
  useEffect(() => {
    loadReportStructure();
    loadAvailableMetrics();
  }, []);

  const loadReportStructure = async () => {
    try {
      const user = JSON.parse(sessionStorage.getItem("userData"));
      const organisation = JSON.parse(sessionStorage.getItem("organisation"));
      const orgId = organisation?.id || user?.user?.LastOrgId || user?.user?.organisation;
      
      if (!user) {
        console.warn("User data not found. Skipping report structure load.");
        setIsLoading(false);
        return;
      }
      
      if (!orgId) {
        console.warn("Organization ID not found. Skipping report structure load.");
        setIsLoading(false);
        return;
      }
      
      const filterUrl = `filters[Organization][id][$eqi]=${orgId}&filters[Metric_Id][$eqi]=Report`;
      const response = await getDataWithFilterUrl("responses", filterUrl);
      
      if (response?.data && response.data.length > 0) {
        const structure = response.data[0]?.attributes?.JsonSchema?.reportStructure || [];
        setReportStructure(structure);
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error loading report structure:", error);
      toast.error("Failed to load report structure. Please refresh the page and try again.", {
        duration: 5000,
      });
      setIsLoading(false);
    }
  };

  const loadAvailableMetrics = async () => {
    try {
      const response = await getAlldata("metrics");
      if (response?.data) {
        setAvailableMetrics(response.data);
      }
    } catch (error) {
      console.error("Error loading metrics:", error);
      toast.error("Failed to load available metrics. Please refresh the page and try again.", {
        duration: 5000,
      });
    }
  };

  const toggleSection = (sectionId) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const addSection = async () => {
    if (!newSection.title.trim()) {
      toast.error("Please enter a section title");
      return;
    }

    if (!validateUserSession()) {
      return;
    }

    try {
      const section = {
        id: `section_${Date.now()}`,
        title: newSection.title,
        description: newSection.description,
        type: newSection.type,
        parentId: newSection.parentId,
        metrics: [],
        children: [],
        createdAt: new Date().toISOString(),
      };

      const updatedStructure = [...reportStructure, section];
      setReportStructure(updatedStructure);
      await saveReportStructure(updatedStructure);
      
      setNewSection({ title: "", description: "", type: "section", parentId: null });
      onSectionModalChange();
    } catch (error) {
      console.error("Error adding section:", error);
      toast.error("Failed to add section. Please try again.", {
        duration: 5000,
      });
    }
  };

  const addMetricToSection = async (sectionId, metricId) => {
    try {
      const updatedStructure = reportStructure.map(section => {
        if (section.id === sectionId) {
          return {
            ...section,
            metrics: [...(section.metrics || []), metricId]
          };
        }
        return section;
      });
      
      setReportStructure(updatedStructure);
      await saveReportStructure(updatedStructure);
    } catch (error) {
      console.error("Error adding metric to section:", error);
      toast.error("Failed to add metric to section. Please try again.", {
        duration: 5000,
      });
    }
  };

  const removeMetricFromSection = async (sectionId, metricId) => {
    try {
      const updatedStructure = reportStructure.map(section => {
        if (section.id === sectionId) {
          return {
            ...section,
            metrics: (section.metrics || []).filter(id => id !== metricId)
          };
        }
        return section;
      });
      
      setReportStructure(updatedStructure);
      await saveReportStructure(updatedStructure);
    } catch (error) {
      console.error("Error removing metric from section:", error);
      toast.error("Failed to remove metric from section. Please try again.", {
        duration: 5000,
      });
    }
  };

  const saveReportStructure = async (structure) => {
    try {
      const user = JSON.parse(sessionStorage.getItem("userData"));
      const organisation = JSON.parse(sessionStorage.getItem("organisation"));
      const orgId = organisation?.id || user?.user?.LastOrgId || user?.user?.organisation;
      const userId = user?.user?.id;

      if (!user) {
        throw new Error("User data not found. Please refresh the page and try again.");
      }
      
      if (!orgId) {
        throw new Error("Organization ID not found. Please refresh the page and try again.");
      }

      const reportData = {
        Metric_Id: "Report",
        Status: "Created",
        JsonSchema: {
          reportStructure: structure,
          lastUpdated: new Date().toISOString(),
        },
        Organization: orgId,
        users_permissions_user: {
          disconnect: [],
          connect: [
            {
              id: userId,
              position: {
                end: true,
              },
            },
          ],
        },
        Logs: [
          {
            by: userId,
            operation: "created",
            timestamp: Date.now(),
          },
        ],
      };

      await PublishData({ data: reportData }, "responses");
    } catch (error) {
      console.error("Error saving report structure:", error);
      const errorMessage = error.message || "Error saving report structure. Please try again.";
      toast.error(errorMessage, {
        duration: 5000,
      });
    }
  };

  const deleteSection = async (sectionId) => {
    if (window.confirm("Are you sure you want to delete this section?")) {
      try {
        const updatedStructure = reportStructure.filter(section => section.id !== sectionId);
        setReportStructure(updatedStructure);
        await saveReportStructure(updatedStructure);
      } catch (error) {
        console.error("Error deleting section:", error);
        toast.error("Failed to delete section. Please try again.", {
          duration: 5000,
        });
      }
    }
  };

  const getMetricName = (metricId) => {
    const metric = availableMetrics.find(m => m.id === metricId);
    return metric?.attributes?.Name || metricId;
  };

  const renderSection = (section, level = 0) => {
    const isExpanded = expandedSections.has(section.id);
    const hasChildren = section.children && section.children.length > 0;
    const sectionMetrics = section.metrics || [];

  return (
      <div key={section.id} className="w-full">
        <div 
          className={`flex items-center gap-3 p-4 border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors ${
            level > 0 ? 'ml-6' : ''
          }`}
        >
          {/* Expand/Collapse Icon */}
          {hasChildren && (
            <button
              onClick={() => toggleSection(section.id)}
              className="p-1 hover:bg-neutral-200 rounded transition-colors"
            >
              <FontAwesomeIcon 
                icon={isExpanded ? faChevronDown : faChevronRight} 
                className="w-4 h-4 text-neutral-600" 
              />
            </button>
          )}
          
          {/* Section Content */}
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <span className="font-medium text-neutral-900">{section.title}</span>
              <Chip size="sm" variant="flat" color="primary">
                {section.type}
              </Chip>
              {sectionMetrics.length > 0 && (
                <Chip size="sm" variant="flat" color="success">
                  {sectionMetrics.length} metrics
                </Chip>
              )}
            </div>
            {section.description && (
              <div className="text-sm text-neutral-600 mt-1">{section.description}</div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="flat"
              onClick={() => {
                setSelectedSection(section);
                onMetricModalOpen();
              }}
                              className="bg-emerald-100 hover:bg-emerald-200 text-emerald-800"
            >
              <FontAwesomeIcon icon={faLink} className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="flat"
              onClick={() => {
                setSelectedSection(section);
                setNewSection({
                  title: section.title,
                  description: section.description,
                  type: section.type,
                  parentId: section.parentId,
                });
                onSectionModalOpen();
              }}
              className="bg-neutral-100 hover:bg-neutral-200 text-neutral-800"
            >
              <FontAwesomeIcon icon={faEdit} className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="flat"
              color="danger"
              onClick={() => deleteSection(section.id)}
              className="bg-red-100 hover:bg-red-200 text-red-800"
            >
              <FontAwesomeIcon icon={faTrash} className="w-3 h-3" />
            </Button>
          </div>
        </div>

        {/* Metrics List */}
        {sectionMetrics.length > 0 && (
          <div className="ml-6 mt-2 space-y-1">
            {sectionMetrics.map(metricId => (
              <div key={metricId} className="flex items-center justify-between p-2 bg-neutral-50 rounded border">
                <span className="text-sm text-neutral-700">{getMetricName(metricId)}</span>
                <Button
                  size="sm"
                  variant="flat"
                  color="danger"
                  onClick={() => removeMetricFromSection(section.id, metricId)}
                  className="bg-red-100 hover:bg-red-200 text-red-800"
                >
                  <FontAwesomeIcon icon={faUnlink} className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Children */}
        {hasChildren && isExpanded && (
          <div className="ml-6 mt-2 space-y-2">
            {section.children.map(child => renderSection(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const filteredMetrics = availableMetrics.filter(metric =>
    metric.attributes?.Name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="w-full">
      <LoadingPage isLoading={isLoading} loadingText="Loading report structure...">
        {/* Report Structure Section */}
        <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-neutral-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">Report Structure</h3>
                <p className="text-sm text-neutral-600 mt-1">Define the sections and subsections of your sustainability report</p>
              </div>
              <div className="flex items-center gap-3">
                <Chip color="primary" variant="flat" size="sm">
                  {reportStructure.length} sections
                </Chip>
                <Button
                  color="primary"
                  variant="flat"
                  onClick={onSectionModalOpen}
                  className="bg-primary-600 hover:bg-primary-700 text-white"
                  startContent={<FontAwesomeIcon icon={faPlus} className="text-white text-sm" />}
                >
                  Add Section
                </Button>
              </div>
            </div>
          </div>
          <div className="p-6">
            {reportStructure.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-4xl text-neutral-300 mb-4">📋</div>
                <p className="text-neutral-500">No report sections defined yet. Click "Add Section" to get started.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {reportStructure.map(section => renderSection(section))}
              </div>
            )}
          </div>
        </div>

        {/* Available Metrics Section */}
        <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-neutral-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">Available Metrics</h3>
                <p className="text-sm text-neutral-600 mt-1">Browse and select metrics to link to report sections</p>
              </div>
              <div className="w-64">
                <Input
                  type="text"
                  variant="bordered"
                  startContent={
                    <SearchIcon
                      className={`text-large text-neutral-400 pointer-events-none flex-shrink-0 `}
                    />
                  }
                  placeholder="Search metrics"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  classNames={{
                    input: "text-neutral-900",
                    inputWrapper: "border-neutral-300 hover:border-neutral-400 focus-within:border-primary-500"
                  }}
                />
              </div>
            </div>
          </div>
          <div className="p-6">
            <Table
              removeWrapper
              selectionMode="single"
              aria-label="metrics-table"
              classNames={{
                th: "bg-neutral-50 text-neutral-700 font-medium",
                td: "py-4",
              }}
            >
              <TableHeader>
                <TableColumn>Metric Name</TableColumn>
                <TableColumn>Category</TableColumn>
                <TableColumn>Description</TableColumn>
                <TableColumn>Actions</TableColumn>
              </TableHeader>
              <TableBody>
                {filteredMetrics.map((metric) => (
                  <TableRow key={metric.id}>
                    <TableCell>
                      <div className="font-medium text-neutral-900">
                        {metric.attributes?.Name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Chip size="sm" variant="flat" color="primary">
                        {metric.attributes?.Category || "General"}
                      </Chip>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-neutral-600">
                        {metric.attributes?.Description || "No description available"}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Dropdown>
                        <DropdownTrigger>
                          <Button
                            size="sm"
                            variant="flat"
                            className="bg-neutral-100 hover:bg-neutral-200 text-neutral-800"
                          >
                            Link to Section
                            <FontAwesomeIcon icon={faChevronDown} className="w-3 h-3 ml-1" />
                          </Button>
                        </DropdownTrigger>
                        <DropdownMenu
                          aria-label="Link to section"
                          onAction={(sectionId) => addMetricToSection(sectionId, metric.id)}
                        >
                          {reportStructure.map((section) => (
                            <DropdownItem key={section.id}>
                              {section.title}
                            </DropdownItem>
                          ))}
                        </DropdownMenu>
                      </Dropdown>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </LoadingPage>

      {/* Add/Edit Section Modal */}
      <Modal isOpen={isSectionModalOpen} onOpenChange={onSectionModalChange}>
        <ModalContent>
          <ModalHeader>
            {selectedSection ? "Edit Section" : "Add New Section"}
          </ModalHeader>
          <ModalBody className="space-y-4">
            <Input
              label="Section Title"
              placeholder="Enter section title"
              value={newSection.title}
              onChange={(e) => setNewSection({...newSection, title: e.target.value})}
            />
            <Input
              label="Description"
              placeholder="Enter section description (optional)"
              value={newSection.description}
              onChange={(e) => setNewSection({...newSection, description: e.target.value})}
            />
            <Dropdown>
              <DropdownTrigger>
                <Button variant="bordered" className="w-full justify-between">
                  {newSection.type.charAt(0).toUpperCase() + newSection.type.slice(1)}
                  <FontAwesomeIcon icon={faChevronDown} className="w-3 h-3" />
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                aria-label="Section type"
                onAction={(type) => setNewSection({...newSection, type})}
              >
                <DropdownItem key="section">Section</DropdownItem>
                <DropdownItem key="subsection">Subsection</DropdownItem>
                <DropdownItem key="metric">Metric Group</DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </ModalBody>
          <ModalFooter>
            <StandardCancelButton onPress={onSectionModalChange}>
              Cancel
            </StandardCancelButton>
            <StandardPrimaryButton onPress={addSection}>
              {selectedSection ? "Update" : "Add"}
            </StandardPrimaryButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Link Metrics Modal */}
      <Modal isOpen={isMetricModalOpen} onOpenChange={onMetricModalChange} size="2xl">
        <ModalContent>
          <ModalHeader>
            Link Metrics to: {selectedSection?.title}
          </ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-neutral-900 mb-2">Available Metrics</h4>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {availableMetrics.map((metric) => (
                    <div
                      key={metric.id}
                      className="flex items-center justify-between p-3 border border-neutral-200 rounded-lg hover:bg-neutral-50"
                    >
                      <div>
                        <div className="font-medium text-neutral-900">
                          {metric.attributes?.Name}
                        </div>
                        <div className="text-sm text-neutral-600">
                          {metric.attributes?.Description || "No description"}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="flat"
                        onClick={() => addMetricToSection(selectedSection.id, metric.id)}
                        className="bg-emerald-100 hover:bg-emerald-200 text-emerald-800"
                      >
                        <FontAwesomeIcon icon={faLink} className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <StandardCloseButton onPress={onMetricModalChange}>
              Close
            </StandardCloseButton>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

// AI Standards Mapping Component
const AIStandardsMapping = () => {
  const [standards] = useState([
    {
      id: 1,
      name: "GRI (Global Reporting Initiative)",
      version: "GRI 2021",
      description: "Comprehensive sustainability reporting framework",
      sections: [
        { id: 1, name: "General Disclosures", metrics: [] },
        { id: 2, name: "Environmental", metrics: [] },
        { id: 3, name: "Social", metrics: [] },
        { id: 4, name: "Governance", metrics: [] }
      ]
    },
    {
      id: 2,
      name: "SASB (Sustainability Accounting Standards Board)",
      version: "SASB 2023",
      description: "Industry-specific sustainability standards",
      sections: [
        { id: 5, name: "Environment", metrics: [] },
        { id: 6, name: "Social Capital", metrics: [] },
        { id: 7, name: "Human Capital", metrics: [] },
        { id: 8, name: "Business Model & Innovation", metrics: [] },
        { id: 9, name: "Leadership & Governance", metrics: [] }
      ]
    },
    {
      id: 3,
      name: "TCFD (Task Force on Climate-related Financial Disclosures)",
      version: "TCFD 2023",
      description: "Climate-related financial risk reporting",
      sections: [
        { id: 10, name: "Governance", metrics: [] },
        { id: 11, name: "Strategy", metrics: [] },
        { id: 12, name: "Risk Management", metrics: [] },
        { id: 13, name: "Metrics and Targets", metrics: [] }
      ]
    }
  ]);
  const [importedStandards, setImportedStandards] = useState([]);
  const [isLoadingStandards, setIsLoadingStandards] = useState(true);

  const [selectedStandard, setSelectedStandard] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [chatMessages, setChatMessages] = useState({});
  const [currentMessage, setCurrentMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [standardToDelete, setStandardToDelete] = useState(null);
  
  const { 
    isOpen: isDeleteModalOpen, 
    onOpen: onDeleteModalOpen, 
    onOpenChange: onDeleteModalChange 
  } = useDisclosure();

  // Utility function to validate user session
  const validateUserSession = () => {
    try {
      const user = JSON.parse(sessionStorage.getItem("userData"));
      
      if (!user) {
        toast.error("Session expired. Please refresh the page and log in again.", {
          duration: 5000,
        });
        return false;
      }
      
      // Check if we have organisation data (either from sessionStorage or user object)
      const organisation = JSON.parse(sessionStorage.getItem("organisation"));
      const hasOrganisation = organisation || (user.user && (user.user.LastOrgId || user.user.organisation));
      
      if (!hasOrganisation) {
        toast.error("Organisation data not found. Please refresh the page and log in again.", {
          duration: 5000,
        });
        return false;
      }
      
      return true;
    } catch (error) {
      console.error("Error validating user session:", error);
      toast.error("Session validation failed. Please refresh the page and log in again.", {
        duration: 5000,
      });
      return false;
    }
  };

  // Load imported standards on component mount
  useEffect(() => {
    loadImportedStandards();
  }, []);

  const loadImportedStandards = async () => {
    setIsLoadingStandards(true);
    const loadingToast = toast.loading("Loading imported standards...");
    
    try {
      const user = JSON.parse(sessionStorage.getItem("userData"));
      const organisation = JSON.parse(sessionStorage.getItem("organisation"));
      const orgId = organisation?.id || user?.user?.LastOrgId || user?.user?.organisation;
      
      if (!user) {
        throw new Error("User data not found. Please refresh the page and try again.");
      }
      
      if (!orgId) {
        throw new Error("Organization ID not found. Please refresh the page and try again.");
      }
      
      const filterUrl = `filters[Organization][id][$eqi]=${orgId}&filters[Metric_Id][$eqi]=Reporting Standards`;
      const response = await getDataWithFilterUrl("responses", filterUrl);
      
      if (response?.data) {
        const imported = response.data
          .filter(item => item.attributes?.JsonSchema?.["Imported From"] === "Tree Structure Import")
          .map(item => {
            const jsonSchema = item.attributes?.JsonSchema || {};
            let parsedStructure = [];
            
            try {
              // Parse the JSON structure from the textarea field
              if (jsonSchema["Json Structure"]) {
                parsedStructure = JSON.parse(jsonSchema["Json Structure"]);
              }
            } catch (parseError) {
              console.error("Error parsing JSON structure for item:", item.id, parseError);
              // Continue with empty structure rather than failing completely
            }
            
            // Convert parsed structure to sections format
            const sections = Array.isArray(parsedStructure) 
              ? parsedStructure.map((section, index) => ({
                  id: `imported_${item.id}_${index}`,
                  name: section.title || section.name || `Section ${index + 1}`,
                  metrics: section.metrics || [],
                  description: section.description || "",
                  type: section.type || "section"
                }))
              : [];

            return {
              id: `imported_${item.id}`,
              name: jsonSchema["Standard Title"] || item.attributes?.Name || "Imported Standard",
              version: jsonSchema["Version"] || "Custom",
              description: jsonSchema["Description"] || "Custom sustainability standard",
              sections: sections,
              isImported: true,
              originalId: item.id
            };
          });
        
        setImportedStandards(imported);
        
        if (imported.length === 0) {
          toast("No imported standards found. Import some structures from the Tree Structure Import tab.", {
            duration: 4000,
          });
        } else {
          toast.success(`Loaded ${imported.length} imported standard(s)`, {
            duration: 2000,
          });
        }
      } else {
        toast("No data found. Import some structures from the Tree Structure Import tab.", {
          duration: 4000,
        });
      }
    } catch (error) {
      console.error("Error loading imported standards:", error);
      const errorMessage = error.message || "Failed to load imported standards. Please try again.";
      toast.error(errorMessage, {
        duration: 5000,
      });
    } finally {
      toast.dismiss(loadingToast);
      setIsLoadingStandards(false);
    }
  };

  const handleDeleteClick = (standard) => {
    setStandardToDelete(standard);
    onDeleteModalOpen();
  };

  const deleteCustomStandard = async () => {
    if (!validateUserSession()) {
      return;
    }

    const loadingToast = toast.loading("Deleting custom standard...");
    
    try {
      const user = JSON.parse(sessionStorage.getItem("userData"));
      const organisation = JSON.parse(sessionStorage.getItem("organisation"));
      const orgId = organisation?.id || user?.user?.LastOrgId || user?.user?.organisation;
      const userId = user?.user?.id;

      // Extract the original database ID from the standard ID
      const dbId = standardToDelete.id.replace('imported_', '');
      
      // Delete the standard from the database
      const deleteResult = await DeleteOnedata("responses", dbId);
      
      if (deleteResult) {
        // Remove from local state
        setImportedStandards(prev => prev.filter(standard => standard.id !== standardToDelete.id));
        
        // Clear selection if the deleted standard was selected
        if (selectedStandard?.id === standardToDelete.id) {
          setSelectedStandard(null);
          setSelectedSection(null);
        }
        
        toast.success("Custom standard deleted successfully!", {
          duration: 3000,
        });
        
        // Close modal and reset state
        onDeleteModalChange();
        setStandardToDelete(null);
      } else {
        throw new Error("Failed to delete from database");
      }
    } catch (error) {
      console.error("Error deleting custom standard:", error);
      toast.error("Failed to delete custom standard. Please try again.", {
        duration: 5000,
      });
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  const sendMessage = async (contextId) => {
    if (!currentMessage.trim()) return;

    const messageId = `${contextId}_${Date.now()}`;
    const newMessage = {
      id: messageId,
      text: currentMessage,
      timestamp: new Date().toISOString(),
      isUser: true,
    };

    setChatMessages(prev => ({
      ...prev,
      [contextId]: [...(prev[contextId] || []), newMessage]
    }));

    setCurrentMessage("");
    setIsLoading(true);

    try {
      // Simulate AI response
      const aiResponse = await generateAIResponse(currentMessage, contextId);
      
      const aiMessage = {
        id: `${contextId}_ai_${Date.now()}`,
        text: aiResponse,
        timestamp: new Date().toISOString(),
        isUser: false,
      };

      setChatMessages(prev => ({
        ...prev,
        [contextId]: [...(prev[contextId] || []), aiMessage]
      }));
    } catch (error) {
      console.error("Error generating AI response:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const generateAIResponse = async (message, contextId) => {
    // Simulate AI response based on context
    const responses = [
      "Based on the GRI standards, this section should include metrics related to energy consumption, greenhouse gas emissions, and water usage.",
      "For SASB compliance, you'll need to report on industry-specific environmental impacts and resource management.",
      "According to TCFD guidelines, this section requires climate scenario analysis and transition risk assessment.",
      "I recommend linking these metrics to demonstrate your organization's sustainability performance and compliance with multiple frameworks.",
      "This section aligns with both GRI and SASB requirements. Consider cross-referencing metrics for comprehensive reporting."
    ];
    
    await new Promise(resolve => setTimeout(resolve, 1000));
    return responses[Math.floor(Math.random() * responses.length)];
  };

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Standards List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
                         <div className="px-6 py-4 border-b border-neutral-200">
               <div className="flex items-center justify-between">
                 <div>
                   <h3 className="text-lg font-semibold text-neutral-900">Sustainability Standards</h3>
                   <p className="text-sm text-neutral-600 mt-1">Select a standard to explore its requirements</p>
                 </div>
                 <Button
                   size="sm"
                   variant="flat"
                   onClick={loadImportedStandards}
                   className="bg-neutral-100 hover:bg-neutral-200 text-neutral-800"
                   startContent={<FontAwesomeIcon icon={faSave} className="w-3 h-3" />}
                 >
                   Refresh
                 </Button>
               </div>
             </div>
                         <div className="p-4 space-y-3">
               {/* Built-in Standards */}
               {standards.map((standard) => (
                 <div
                   key={standard.id}
                   className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                     selectedStandard?.id === standard.id
                       ? 'border-primary-500 bg-primary-50'
                       : 'border-neutral-200 hover:border-neutral-300'
                   }`}
                   onClick={() => setSelectedStandard(standard)}
                 >
                   <div className="flex items-center gap-2">
                     <div className="font-medium text-neutral-900">{standard.name}</div>
                     <Chip size="sm" variant="flat" color="primary">Built-in</Chip>
                   </div>
                   <div className="text-sm text-neutral-600">{standard.version}</div>
                   <div className="text-xs text-neutral-500 mt-1">{standard.description}</div>
                 </div>
               ))}
               
               {/* Imported Standards */}
               {importedStandards.map((standard) => (
                 <div
                   key={standard.id}
                   className={`p-4 border rounded-lg transition-colors ${
                     selectedStandard?.id === standard.id
                       ? 'border-primary-500 bg-primary-50'
                       : 'border-neutral-200 hover:border-neutral-300'
                   }`}
                 >
                   <div 
                     className="cursor-pointer"
                     onClick={() => setSelectedStandard(standard)}
                   >
                     <div className="flex items-center justify-between">
                       <div className="flex items-center gap-2">
                         <div className="font-medium text-neutral-900">{standard.name}</div>
                         <Chip size="sm" variant="flat" color="success">Custom</Chip>
                       </div>
                       <Button
                         size="sm"
                         variant="flat"
                         color="danger"
                         onClick={(e) => {
                           e.stopPropagation();
                           handleDeleteClick(standard);
                         }}
                         className="min-w-0 p-2"
                       >
                         <FontAwesomeIcon icon={faTrash} className="w-3 h-3" />
                       </Button>
                     </div>
                     <div className="text-sm text-neutral-600">{standard.version}</div>
                     <div className="text-xs text-neutral-500 mt-1">{standard.description}</div>
                   </div>
                 </div>
               ))}
               
               {isLoadingStandards && (
                 <div className="text-center py-4">
                   <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
                   <p className="text-sm text-neutral-500 mt-2">Loading standards...</p>
                 </div>
               )}
             </div>
          </div>
        </div>

        {/* Sections and Chat */}
        <div className="lg:col-span-2">
          {selectedStandard ? (
            <div className="space-y-6">
              {/* Standard Sections */}
              <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-neutral-200">
                  <h3 className="text-lg font-semibold text-neutral-900">
                    {selectedStandard.name} Sections
                  </h3>
                  <p className="text-sm text-neutral-600 mt-1">
                    Explore sections and get AI assistance for metric mapping
                  </p>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {selectedStandard.sections.map((section) => (
                      <div
                        key={section.id}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                          selectedSection?.id === section.id
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-neutral-200 hover:border-neutral-300'
                        }`}
                        onClick={() => setSelectedSection(section)}
                      >
                        <div className="font-medium text-neutral-900">{section.name}</div>
                        <div className="text-sm text-neutral-600 mt-1">
                          {section.metrics.length} metrics linked
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* AI Chat */}
              {selectedSection && (
                <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-neutral-200">
                    <h3 className="text-lg font-semibold text-neutral-900">
                      AI Assistant - {selectedSection.name}
                    </h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      Ask questions about metric mapping and compliance requirements
                    </p>
                  </div>
                  <div className="p-4">
                    <div className="flex flex-col h-96">
                      <div className="flex-1 overflow-y-auto space-y-3 mb-4 p-3 bg-neutral-50 rounded-lg">
                        {(chatMessages[selectedSection.id] || []).map((message) => (
                          <div
                            key={message.id}
                            className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-xs p-3 rounded-lg ${
                                message.isUser
                                  ? 'bg-primary-600 text-white'
                                  : 'bg-white border border-neutral-200 text-neutral-900'
                              }`}
                            >
                              <div className="text-sm">{message.text}</div>
                              <div className="text-xs opacity-70 mt-1">
                                {new Date(message.timestamp).toLocaleTimeString()}
                              </div>
                            </div>
                          </div>
                        ))}
                        {isLoading && (
                          <div className="flex justify-start">
                            <div className="bg-white border border-neutral-200 text-neutral-900 p-3 rounded-lg">
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                                <span className="text-sm">AI is thinking...</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        <Input
                          size="sm"
                          placeholder="Ask about metric mapping, compliance requirements..."
                          value={currentMessage}
                          onChange={(e) => setCurrentMessage(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              sendMessage(selectedSection.id);
                            }
                          }}
                          className="flex-1"
                        />
                        <Button
                          size="sm"
                          color="primary"
                          onClick={() => sendMessage(selectedSection.id)}
                          disabled={!currentMessage.trim() || isLoading}
                          startContent={<FontAwesomeIcon icon={faRobot} className="w-3 h-3" />}
                        >
                          Ask AI
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
              <div className="text-center py-12">
                <FontAwesomeIcon icon={faComments} className="text-4xl text-neutral-300 mb-4" />
                <p className="text-neutral-500">Select a sustainability standard to start exploring with AI assistance</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onOpenChange={onDeleteModalChange}>
        <ModalContent>
          <ModalHeader>Delete Custom Standard</ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-full">
                  <FontAwesomeIcon icon={faTrash} className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-neutral-900 font-medium">Delete "{standardToDelete?.name}"?</p>
                  <p className="text-sm text-neutral-600">This action cannot be undone.</p>
                </div>
              </div>
              <div className="bg-neutral-50 p-4 rounded-lg">
                <div className="text-sm text-neutral-600">
                  <p><strong>Standard:</strong> {standardToDelete?.name}</p>
                  <p><strong>Version:</strong> {standardToDelete?.version || "Custom"}</p>
                  <p><strong>Description:</strong> {standardToDelete?.description || "No description"}</p>
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <StandardCancelButton onPress={onDeleteModalChange}>
              Cancel
            </StandardCancelButton>
            <Button 
              color="danger" 
              onPress={deleteCustomStandard}
              startContent={<FontAwesomeIcon icon={faTrash} className="w-3 h-3" />}
            >
              Delete Standard
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

// Tree Structure Import Component
const TreeStructureImport = () => {
  const [jsonInput, setJsonInput] = useState("");
  const [parsedStructure, setParsedStructure] = useState(null);
  const [isValidJson, setIsValidJson] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [structureTitle, setStructureTitle] = useState("");
  const [structureVersion, setStructureVersion] = useState("");
  const [structureDescription, setStructureDescription] = useState("");

  const { 
    isOpen: isImportModalOpen, 
    onOpen: onImportModalOpen, 
    onOpenChange: onImportModalChange 
  } = useDisclosure();

  // Utility function to validate user session
  const validateUserSession = () => {
    try {
      const user = JSON.parse(sessionStorage.getItem("userData"));
      
      if (!user) {
        toast.error("Session expired. Please refresh the page and log in again.", {
          duration: 5000,
        });
        return false;
      }
      
      // Check if we have organisation data (either from sessionStorage or user object)
      const organisation = JSON.parse(sessionStorage.getItem("organisation"));
      const hasOrganisation = organisation || (user.user && (user.user.LastOrgId || user.user.organisation));
      
      if (!hasOrganisation) {
        toast.error("Organisation data not found. Please refresh the page and log in again.", {
          duration: 5000,
        });
        return false;
      }
      
      return true;
    } catch (error) {
      console.error("Error validating user session:", error);
      toast.error("Session validation failed. Please refresh the page and log in again.", {
        duration: 5000,
      });
      return false;
    }
  };

  // Function to convert tree structure to sections
  const convertTreeToSections = (node, level = 0, parentId = null) => {
    const nodeId = node.id || node.title || `section_${Date.now()}_${Math.random()}`;
    const nodeTitle = node.title || node.name || node.key || "Untitled";
    
    // Determine section type based on hierarchy level
    let sectionType = "section";
    if (level === 1) sectionType = "subsection";
    else if (level === 2) sectionType = "sub-subsection";
    else if (level >= 3) sectionType = `level-${level}-subsection`;
    
    const section = {
      id: nodeId,
      title: nodeTitle,
      description: node.description || "",
      type: sectionType,
      level: level,
      parentId: parentId,
      metrics: node.metrics || [],
      children: [],
      createdAt: new Date().toISOString(),
    };

    // Recursively convert children to subsections (unlimited depth)
    if (node.children && Array.isArray(node.children)) {
      section.children = node.children.map(child => 
        convertTreeToSections(child, level + 1, nodeId)
      );
    }

    return section;
  };

  const validateAndParseJson = (input) => {
    try {
      const parsed = JSON.parse(input);
      
      // Convert tree structure to sections format
      let sections = [];
      
      if (Array.isArray(parsed)) {
        // If it's an array, convert each item to a section
        sections = parsed.map(item => convertTreeToSections(item, 0));
      } else {
        // If it's a single object, convert it to a section
        sections = [convertTreeToSections(parsed, 0)];
      }
      
      setParsedStructure(sections);
      setIsValidJson(true);
      
      // Show success message with section count
      const totalSections = sections.length;
      
      // Count all subsections at all levels
      const countAllSubsections = (sections) => {
        return sections.reduce((count, section) => {
          let sectionCount = 0;
          if (section.children && section.children.length > 0) {
            sectionCount = section.children.length + countAllSubsections(section.children);
          }
          return count + sectionCount;
        }, 0);
      };
      
      const totalSubsections = countAllSubsections(sections);
      
      toast.success(`Parsed ${totalSections} sections and ${totalSubsections} subsections from tree structure`, {
        duration: 3000,
      });
      
      return true;
    } catch (error) {
      setParsedStructure(null);
      setIsValidJson(false);
      toast.error(`Invalid JSON format: ${error.message}`, {
        duration: 4000,
      });
      return false;
    }
  };

  const handleJsonInputChange = (value) => {
    setJsonInput(value);
    if (value.trim()) {
      validateAndParseJson(value);
    } else {
      setParsedStructure(null);
      setIsValidJson(true);
    }
  };

  const handlePasteJson = () => {
    navigator.clipboard.readText().then((text) => {
      setJsonInput(text);
      validateAndParseJson(text);
    }).catch((err) => {
      console.error('Failed to read clipboard:', err);
      toast.error("Failed to read from clipboard. Please paste manually.", {
        duration: 4000,
      });
    });
  };

  const handleClearInput = () => {
    setJsonInput("");
    setParsedStructure(null);
    setIsValidJson(true);
    setStructureTitle("");
    setStructureVersion("");
    setStructureDescription("");
  };

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const renderTreeNode = (node, level = 0, path = "") => {
    const nodeId = path || node.id || `node_${level}_${Math.random()}`;
    const nodeTitle = node.title || "Untitled";
    const isExpanded = expandedNodes.has(nodeId);
    const hasChildren = node.children && node.children.length > 0;

    return (
      <div key={nodeId} className="w-full">
        <div 
          className={`flex items-center gap-3 p-3 border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors ${
            level > 0 ? 'ml-6' : ''
          }`}
        >
          {/* Expand/Collapse Icon */}
          {hasChildren && (
            <button
              onClick={() => toggleNode(nodeId)}
              className="p-1 hover:bg-neutral-200 rounded transition-colors"
            >
              <FontAwesomeIcon 
                icon={isExpanded ? faChevronDown : faChevronRight} 
                className="w-4 h-4 text-neutral-600" 
              />
            </button>
          )}
          
          {/* Node Content */}
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <span className="font-medium text-neutral-900">{nodeTitle}</span>
              <Chip size="sm" variant="flat" color={level === 0 ? "primary" : level === 1 ? "secondary" : "default"}>
                {level === 0 ? "Section" : level === 1 ? "Subsection" : level === 2 ? "Sub-subsection" : `Level ${level}`}
              </Chip>
              {node.metrics && node.metrics.length > 0 && (
                <Chip size="sm" variant="flat" color="success">
                  {node.metrics.length} metrics
                </Chip>
              )}
            </div>
            {node.description && (
              <div className="text-sm text-neutral-600 mt-1">{node.description}</div>
            )}
          </div>
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div className="ml-6 mt-2 space-y-2">
            {node.children.map((child, index) => 
              renderTreeNode(child, level + 1, `${nodeId}_${index}`)
            )}
          </div>
        )}
      </div>
    );
  };

  const saveStructureLocally = () => {
    try {
      if (!parsedStructure) {
        toast.error("No structure to save");
        return;
      }

      if (!structureTitle.trim()) {
        toast.error("Please provide a title for the structure");
        return;
      }

      const dataToSave = {
        title: structureTitle,
        version: structureVersion || "Custom",
        description: structureDescription || "Custom sustainability standard",
        structure: parsedStructure,
        exportedAt: new Date().toISOString(),
      };

      const blob = new Blob([JSON.stringify(dataToSave, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${structureTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_structure.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success("Structure saved locally successfully!", {
        duration: 3000,
      });
    } catch (error) {
      console.error("Error saving structure locally:", error);
      toast.error("Failed to save structure locally. Please try again.", {
        duration: 4000,
      });
    }
  };

  const importStructure = async () => {
    if (!parsedStructure) {
      toast.error("Please provide valid JSON structure");
      return;
    }

    if (!structureTitle.trim()) {
      toast.error("Please provide a title for the structure");
      return;
    }

    if (!validateUserSession()) {
      return;
    }

    setIsLoading(true);
    const loadingToast = toast.loading("Importing structure...");
    
    try {
      const user = JSON.parse(sessionStorage.getItem("userData"));
      const organisation = JSON.parse(sessionStorage.getItem("organisation"));
      const orgId = organisation?.id || user?.user?.LastOrgId || user?.user?.organisation;
      const userId = user?.user?.id;

      // Save using the "Reporting Standards" metric structure
      const submissionData = {
        Metric_Id: "Reporting Standards",
        Status: "Created",
        JsonSchema: {
          "Json Structure": JSON.stringify(parsedStructure, null, 2),
          "Standard Title": structureTitle,
          "Version": structureVersion || "Custom",
          "Description": structureDescription || "Custom sustainability standard",
          "Imported From": "Tree Structure Import",
          "Last Updated": new Date().toISOString(),
        },
        Organization: orgId,
        users_permissions_user: {
          disconnect: [],
          connect: [
            {
              id: userId,
              position: {
                end: true,
              },
            },
          ],
        },
        Logs: [
          {
            by: userId,
            operation: "created",
            timestamp: Date.now(),
          },
        ],
      };

      await PublishData({ data: submissionData }, "responses");
      toast.success("Structure imported successfully! You can now find it in the AI Standards Mapping tab.", {
        duration: 4000,
      });
      onImportModalChange();
      handleClearInput();
    } catch (error) {
      console.error("Error importing structure:", error);
      const errorMessage = error.message || "Failed to import structure. Please try again.";
      toast.error(errorMessage, {
        duration: 5000,
      });
    } finally {
      toast.dismiss(loadingToast);
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                 {/* JSON Input Section */}
         <div className="space-y-6">
           {/* Structure Information */}
           <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
             <div className="px-6 py-4 border-b border-neutral-200">
               <h3 className="text-lg font-semibold text-neutral-900">Structure Information</h3>
               <p className="text-sm text-neutral-600 mt-1">Define the standard details</p>
             </div>
             <div className="p-6 space-y-4">
               <Input
                 label="Standard Title"
                 placeholder="e.g., Custom Sustainability Framework"
                 value={structureTitle}
                 onChange={(e) => setStructureTitle(e.target.value)}
                 isRequired
                 classNames={{
                   input: "text-neutral-900",
                   inputWrapper: "border-neutral-300 hover:border-neutral-400 focus-within:border-primary-500"
                 }}
               />
               <Input
                 label="Version"
                 placeholder="e.g., v1.0, 2024"
                 value={structureVersion}
                 onChange={(e) => setStructureVersion(e.target.value)}
                 classNames={{
                   input: "text-neutral-900",
                   inputWrapper: "border-neutral-300 hover:border-neutral-400 focus-within:border-primary-500"
                 }}
               />
               <Input
                 label="Description"
                 placeholder="Brief description of the standard"
                 value={structureDescription}
                 onChange={(e) => setStructureDescription(e.target.value)}
                 classNames={{
                   input: "text-neutral-900",
                   inputWrapper: "border-neutral-300 hover:border-neutral-400 focus-within:border-primary-500"
                 }}
               />
             </div>
           </div>

           <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-neutral-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900">Import Tree Structure</h3>
                  <p className="text-sm text-neutral-600 mt-1">Paste JSON structure to import report sections</p>
                </div>
                <div className="flex items-center gap-2">
                  <Tooltip content="Paste from clipboard">
                    <Button
                      size="sm"
                      variant="flat"
                      onClick={handlePasteJson}
                      className="bg-neutral-100 hover:bg-neutral-200 text-neutral-800"
                    >
                      <FontAwesomeIcon icon={faPaste} className="w-3 h-3" />
                    </Button>
                  </Tooltip>
                  <Tooltip content="Clear input">
                    <Button
                      size="sm"
                      variant="flat"
                      color="danger"
                      onClick={handleClearInput}
                      className="bg-red-100 hover:bg-red-200 text-red-800"
                    >
                      <FontAwesomeIcon icon={faTrash} className="w-3 h-3" />
                    </Button>
                  </Tooltip>
                </div>
              </div>
            </div>
            <div className="p-6">
              <Textarea
                label="JSON Structure"
                placeholder="Paste your JSON structure here..."
                value={jsonInput}
                onChange={(e) => handleJsonInputChange(e.target.value)}
                minRows={15}
                maxRows={20}
                className={`w-full ${!isValidJson && jsonInput.trim() ? 'border-red-500' : ''}`}
                classNames={{
                  input: "text-neutral-900 font-mono text-sm",
                  inputWrapper: !isValidJson && jsonInput.trim() 
                    ? "border-red-500 hover:border-red-600 focus-within:border-red-600" 
                    : "border-neutral-300 hover:border-neutral-400 focus-within:border-primary-500"
                }}
              />
              {!isValidJson && jsonInput.trim() && (
                <div className="text-red-600 text-sm mt-2">
                  Invalid JSON format. Please check your input.
                </div>
              )}
              {isValidJson && parsedStructure && (
                <div className="text-green-600 text-sm mt-2">
                  ✓ Valid JSON structure detected
                </div>
              )}
            </div>
          </div>

          {/* Import Actions */}
          <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-neutral-200">
              <h3 className="text-lg font-semibold text-neutral-900">Import Actions</h3>
            </div>
            <div className="p-6 space-y-4">
                             <div className="flex gap-3">
                 <Button
                   color="primary"
                   variant="flat"
                   onClick={() => setPreviewMode(!previewMode)}
                   className="bg-primary-600 hover:bg-primary-700 text-white"
                   startContent={<FontAwesomeIcon icon={faTree} className="text-white text-sm" />}
                   disabled={!parsedStructure}
                 >
                   {previewMode ? "Hide Preview" : "Show Preview"}
                 </Button>
                 <Button
                   color="success"
                   variant="flat"
                   onClick={onImportModalOpen}
                   className="bg-green-600 hover:bg-green-700 text-white"
                   startContent={<FontAwesomeIcon icon={faUpload} className="text-white text-sm" />}
                   disabled={!parsedStructure}
                 >
                   Import Structure
                 </Button>
                 {/* <Button
                   color="secondary"
                   variant="flat"
                   onClick={saveStructureLocally}
                   className="bg-purple-600 hover:bg-purple-700 text-white"
                   startContent={<FontAwesomeIcon icon={faSave} className="text-white text-sm" />}
                   disabled={!parsedStructure || !structureTitle.trim()}
                 >
                   Save Locally
                 </Button> */}
               </div>
              
                             <div className="text-sm text-neutral-600">
                 <p><strong>Expected JSON Format:</strong></p>
                 <pre className="bg-neutral-50 p-3 rounded text-xs mt-2 overflow-x-auto">
{`[
  {
    "id": "section_1",
    "title": "Environmental Performance",
    "description": "Environmental impact metrics",
    "type": "section",
    "metrics": [],
    "children": [
      {
        "id": "subsection_1",
        "title": "Carbon Emissions",
        "description": "GHG emissions data",
        "type": "subsection",
        "metrics": []
      }
    ]
  }
]`}
                 </pre>
                 <p className="mt-2 text-xs text-neutral-500">
                   <strong>Note:</strong> The imported structure will appear as a custom standard in the AI Standards Mapping tab.
                 </p>
               </div>
            </div>
          </div>
        </div>

        {/* Preview Section */}
        <div className="space-y-6">
          {previewMode && parsedStructure ? (
            <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-neutral-200">
                <h3 className="text-lg font-semibold text-neutral-900">Structure Preview</h3>
                <p className="text-sm text-neutral-600 mt-1">Preview of the imported structure</p>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                  {parsedStructure && Array.isArray(parsedStructure) 
                    ? parsedStructure.map((node, index) => renderTreeNode(node, 0, `root_${index}`))
                    : parsedStructure 
                    ? renderTreeNode(parsedStructure, 0, "root")
                    : <div className="text-center py-8 text-neutral-500">No structure to preview</div>
                  }
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
              <div className="text-center py-12">
                <FontAwesomeIcon icon={faTree} className="text-4xl text-neutral-300 mb-4" />
                <p className="text-neutral-500">
                  {parsedStructure 
                    ? "Click 'Show Preview' to see the structure" 
                    : "Paste JSON structure to see preview"
                  }
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Import Confirmation Modal */}
      <Modal isOpen={isImportModalOpen} onOpenChange={onImportModalChange}>
        <ModalContent>
          <ModalHeader>Import Report Structure</ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <p className="text-neutral-600">
                Are you sure you want to import this structure? This will replace any existing report structure.
              </p>
              <div className="bg-neutral-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-neutral-900 mb-2">Structure Summary:</div>
                <div className="text-sm text-neutral-600 space-y-1">
                  {parsedStructure && Array.isArray(parsedStructure) ? (
                    <>
                      <div>• {parsedStructure.length} main sections</div>
                      <div>• {(() => {
                        const countAllSubsections = (sections) => {
                          return sections.reduce((count, section) => {
                            let sectionCount = 0;
                            if (section?.children && section.children.length > 0) {
                              sectionCount = section.children.length + countAllSubsections(section.children);
                            }
                            return count + sectionCount;
                          }, 0);
                        };
                        return countAllSubsections(parsedStructure);
                      })()} subsections</div>
                      <div>• {parsedStructure.reduce((count, section) => 
                        count + (section?.metrics ? section.metrics.length : 0), 0
                      )} linked metrics</div>
                    </>
                  ) : parsedStructure ? (
                    <>
                      <div>• 1 main section</div>
                      <div>• {(() => {
                        const countAllSubsections = (sections) => {
                          return sections.reduce((count, section) => {
                            let sectionCount = 0;
                            if (section?.children && section.children.length > 0) {
                              sectionCount = section.children.length + countAllSubsections(section.children);
                            }
                            return count + sectionCount;
                          }, 0);
                        };
                        return parsedStructure?.children ? countAllSubsections([parsedStructure]) : 0;
                      })()} subsections</div>
                      <div>• {parsedStructure?.metrics ? parsedStructure.metrics.length : 0} linked metrics</div>
                    </>
                  ) : (
                    <div>No structure to import</div>
                  )}
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <StandardCancelButton onPress={onImportModalChange}>
              Cancel
            </StandardCancelButton>
            <Button 
              color="primary" 
              onPress={importStructure}
              isLoading={isLoading}
            >
              Import Structure
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

// Main StandardsMapping Component
const StandardsMapping = () => {
  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Page Header */}
      <div className="bg-white border-b border-neutral-200 px-8 py-6 shadow-sm">
        <div className="flex items-center justify-between max-w-full">
          <div>
            <h1 className="text-2xl font-semibold text-neutral-900">Standards Mapping</h1>
            <p className="text-sm text-neutral-600 mt-1">Define your sustainability report structure and explore standards with AI assistance</p>
          </div>
        </div>
      </div>

      <BreadCrumbsPage>
        <div className="w-full px-2 py-8">
          <Tabs 
            aria-label="Standards Mapping Options" 
            className="w-full"
            classNames={{
              tabList: "gap-6 w-full relative rounded-none p-0 border-b border-neutral-200",
              cursor: "w-full bg-slate-100",
              tab: "max-w-fit px-2 h-12",
              tabContent: "group-data-[selected=true]:text-primary-600"
            }}
          >
            <Tab
              key="tree-import"
              title={
                <div className="flex items-center space-x-2">
                  <FontAwesomeIcon icon={faTree} className="w-4 h-4" />
                  <span>Add a Reporting Standards</span>
                </div>
              }
            >
              <TreeStructureImport />
            </Tab>
            <Tab
              key="report-structure"
              title={
                <div className="flex items-center space-x-2">
                  <FontAwesomeIcon icon={faLink} className="w-4 h-4" />
                  <span>Report Structure</span>
                </div>
              }
            >
              <ReportStructure />
            </Tab>
            <Tab
              key="ai-mapping"
              title={
                <div className="flex items-center space-x-2">
                  <FontAwesomeIcon icon={faRobot} className="w-4 h-4" />
                  <span>AI Standards Mapping</span>
                </div>
              }
            >
              <AIStandardsMapping />
            </Tab>
            
          </Tabs>
        </div>
      </BreadCrumbsPage>
      <Toaster />
    </div>
  );
};

export default StandardsMapping; 